import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import CardActions from '@mui/joy/CardActions'
import CardContent from '@mui/joy/CardContent'
import Modal, { type ModalProps } from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import Typography from '@mui/joy/Typography'
import { type FC } from 'react'
import { useTranslation } from 'react-i18next'

import { type ConfirmationOptions } from '../../useConfirm.types'

export interface ConfirmationModalProps extends Omit<ModalProps, 'children'> {
  options?: ConfirmationOptions
  onConfirm: () => void
  onDecline: () => void
}

const DEFAULT_CONFIRM_OPTION_KEYS = {
  title: 'dialog.confirm.title',
  question: '',
  cancel: 'common.cancel',
  confirm: 'common.confirm',
} as const

export const ConfirmModal: FC<ConfirmationModalProps> = ({
  options,
  onConfirm,
  onDecline,
  sx,
  ...props
}) => {
  const { t } = useTranslation()

  const {
    title = t(DEFAULT_CONFIRM_OPTION_KEYS.title),
    question = DEFAULT_CONFIRM_OPTION_KEYS.question,
    cancel = t(DEFAULT_CONFIRM_OPTION_KEYS.cancel),
    confirm = t(DEFAULT_CONFIRM_OPTION_KEYS.confirm),
  } = options ?? {}

  return (
    <Modal
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...sx,
      }}
      {...props}
    >
      <Card
        sx={{
          width: 346,
          display: 'flex',
          alignItems: 'flex-start',
          padding: 2.5,
          border: 'none',
        }}
      >
        <ModalClose sx={{ zIndex: 2 }} />
        <CardContent>
          <Typography
            component="h4"
            level="title-md"
            fontWeight="lg"
            sx={{
              textAlign: 'left',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              width: '300px',
            }}
          >
            {title}
          </Typography>
          <Typography level="body-sm" sx={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}>
            {question}
          </Typography>
        </CardContent>
        <CardActions sx={{ gap: 2, width: '100%', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="outlined" onClick={onDecline}>
            {cancel}
          </Button>
          <Button size="sm" color="warning" onClick={onConfirm}>
            {confirm}
          </Button>
        </CardActions>
      </Card>
    </Modal>
  )
}
